/*
 * newsbot - forked from autobrr
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";

import { useTrends } from "@api/queries";
import { classNames } from "@utils";
import { getFaviconUrl } from "@utils/favicon";
import { EmptySimple } from "@components/emptystates";
import type { TrendingEntity } from "@/types";

const VERTICALS = ["crypto", "poker", "gambling", "sports"] as const;
const WINDOWS = ["24h", "7d"] as const;

// Forked verbatim from FeedHealthTable.tsx SuccessRateBadge.
function MomentumBadge({ row }: { row: TrendingEntity }) {
  if (row.is_new) {
    return (
      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        NEW
      </span>
    );
  }
  const pct = row.pct_change ?? 0;
  const up = pct >= 0;
  const cls = up
    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
  return (
    <span className={classNames("px-2 py-0.5 rounded-md text-xs font-medium", cls)}>
      {up ? "↑" : "↓"} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

// Forked verbatim from Stories.tsx vertical-tab button.
function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
        active
          ? "border-blue-500 text-blue-600 dark:text-blue-400"
          : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
      )}
    >
      {children}
    </button>
  );
}

export function Trends() {
  const [vertical, setVertical] = useState<string>("crypto");
  const [window, setWindow] = useState<string>("7d");
  const { data, isLoading } = useTrends(vertical, window);

  return (
    <main aria-label="Trends">
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-black dark:text-white">Trends</h1>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-6 px-2 sm:px-6 lg:pb-16 lg:px-8">
        {/* Vertical Tabs — forked from Stories.tsx */}
        <div className="relative flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
          {VERTICALS.map((v) => (
            <TabButton key={v} active={vertical === v} onClick={() => setVertical(v)}>
              <span className="capitalize">{v}</span>
            </TabButton>
          ))}
          <div className="flex-1" />
          {WINDOWS.map((w) => (
            <TabButton key={w} active={window === w} onClick={() => setWindow(w)}>
              {w}
            </TabButton>
          ))}
        </div>

        {isLoading ? (
          <div className="animate-pulse text-gray-500">Loading...</div>
        ) : !data || data.entities.length === 0 ? (
          <EmptySimple title="No trending entities" subtitle="Try a different window or vertical" />
        ) : (
          <ul className="min-w-full relative">
            <li className="grid grid-cols-12 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
              <div className="col-span-3 py-3 pl-4">Entity</div>
              <div className="col-span-1 py-3">Stories</div>
              <div className="col-span-2 py-3">Momentum</div>
              <div className="col-span-5 py-3">Top Story</div>
              <div className="col-span-1 py-3">Imp</div>
            </li>
            {data.entities.map((row) => (
              <li
                key={row.entity}
                className="grid grid-cols-12 items-center text-sm text-gray-900 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800"
              >
                <div className="col-span-3 py-3 pl-4 truncate font-medium">
                  <Link
                    to="/stories"
                    search={{
                      page: 0,
                      sort: "last_article_at",
                      dir: "desc",
                      q: row.entity,
                      minImportance: undefined,
                      category: "",
                      tags: "",
                      vertical,
                    }}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                    title={`See ${row.entity} stories`}
                  >
                    {row.entity}
                  </Link>
                </div>
                <div className="col-span-1 py-3">{row.story_count}</div>
                <div className="col-span-2 py-3">
                  <MomentumBadge row={row} />
                </div>
                <div className="col-span-5 py-3 pr-3 flex items-center gap-2 min-w-0">
                  {row.top_source_domain && (
                    <img
                      src={getFaviconUrl(row.top_source_domain)}
                      alt=""
                      className="w-4 h-4 shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <span className="truncate text-gray-600 dark:text-gray-400" title={row.top_headline ?? ""}>
                    {row.top_headline ?? "—"}
                  </span>
                </div>
                <div className="col-span-1 py-3 text-gray-500">
                  {row.avg_importance.toFixed(2)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
